import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Copy, Check, AlertTriangle, X, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { rotateApiKey } from '../api/apiKeys'
import type { RotateApiKeyResponse } from '../types/apiKey.types'

interface Props {
  accountId: string
  keyId: string
  keyName: string
  onClose: () => void
}

export function RotateKeyModal({ accountId, keyId, keyName, onClose }: Props) {
  const queryClient = useQueryClient()
  const [gracePeriodHours, setGracePeriodHours] = useState(24)
  const [result, setResult] = useState<RotateApiKeyResponse | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirmed, setConfirmed] = useState(false)

  const mutation = useMutation({
    mutationFn: () => rotateApiKey(accountId, keyId, { gracePeriodHours }),
    onSuccess: (data) => {
      setResult(data)
      queryClient.invalidateQueries({ queryKey: ['api-keys', accountId] })
    },
  })

  const handleCopy = async () => {
    if (!result) return
    await navigator.clipboard.writeText(result.newSecret)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
              <RefreshCw className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Key Rotated</h2>
              <p className="text-sm text-gray-500">{keyName}</p>
            </div>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">{result.warning}</p>
            </div>
          </div>

          <div className="mb-4 text-sm text-gray-600">
            Previous secret valid until:{' '}
            <span className="font-medium">{new Date(result.previousSecretValidUntil).toLocaleString()}</span>
          </div>

          <div className="mb-6">
            <p className="text-xs font-medium text-gray-500 mb-1">New Secret (copy now — never shown again)</p>
            <div className="flex gap-2">
              <code className="flex-1 font-mono text-sm bg-gray-900 text-green-400 rounded px-3 py-2 overflow-auto break-all">
                {result.newSecret}
              </code>
              <Button variant="outline" size="icon" onClick={handleCopy}>
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <label className="flex items-center gap-2 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">I have securely saved the new secret</span>
          </label>

          <Button onClick={() => confirmed && onClose()} disabled={!confirmed} className="w-full">
            Done
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Rotate API Key</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-5">
          Rotating <span className="font-medium">{keyName}</span> will generate a new secret.
          The old secret will remain valid during the grace period.
        </p>

        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Grace period (hours)
          </label>
          <Input
            type="number"
            min={0}
            max={168}
            value={gracePeriodHours}
            onChange={(e) => setGracePeriodHours(Number(e.target.value))}
          />
          <p className="text-xs text-gray-400 mt-1">0–168 hours (7 days max). Default: 24h.</p>
        </div>

        {mutation.isError && (
          <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2 mb-4">
            {(mutation.error as Error).message}
          </p>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? 'Rotating…' : 'Rotate Key'}
          </Button>
        </div>
      </div>
    </div>
  )
}
